import { flow, set } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { ControlBarView, PdfActionTypes, PDFSearchType, PdfState } from './types';

const initialSearchSettings = { searchType: PDFSearchType.EXACT, scheduleOnly: true };

export const INITIAL_STATE: PdfState = {
    pdfText: [],
    pdfSearchTerm: '',
    currentPdfPage: 1,
    searchResults: [],
    selectedSearchResult: null,
    searchSettings: initialSearchSettings,
    savedSearchSettings: initialSearchSettings,
    scheduleStartPage: 1,
    scrollToInitialResult: false,
    controlBarView: ControlBarView.SEARCH,
    numberOfPages: null,
    pageHighlightInfo: undefined,
    checkingPageHighlights: false,
    type: null
};

export const pdfReducer: Reducer<PdfState> = (state = INITIAL_STATE, { payload, type }): PdfState => {
    switch (type) {
        case PdfActionTypes.SET_PDF_TEXT: {
            const pageAlreadyIncluded = !!state.pdfText.find(({ pageNumber, textItems }) => pageNumber === payload.pageNumber && textItems.length === payload.textItems.length);
            const updatedScheduleText = pageAlreadyIncluded ? state.pdfText : [...state.pdfText, payload];
            return set('pdfText', updatedScheduleText, state);
        }
        case PdfActionTypes.SET_PDF_SEARCH_TERM: {
            if (payload === '') {
                return flow(
                    set('pdfSearchTerm', payload),
                    set('selectedSearchResult', null),
                    set('searchResults', []),
                    set('pageHighlightInfo', undefined)
                )(state);
            }
            return flow(
                set('pdfSearchTerm', payload),
                set('selectedSearchResult', null),
                set('pageHighlightInfo', undefined)
            )(state);
        }
        case PdfActionTypes.SET_CURRENT_PDF_PAGE:
            return set('currentPdfPage', payload, state);
        case PdfActionTypes.SET_SCHEDULE_START_PAGE:
            return set('scheduleStartPage', payload, state);
        case PdfActionTypes.SET_PDF_SEARCH_RESULTS:
            return flow(
                set('searchResults', payload),
                set('selectedSearchResult', payload[0]),
                set('scrollToInitialResult', true)
            )(state);
        case PdfActionTypes.SET_SCROLL_TO_INITIAL_RESULT:
            return set('scrollToInitialResult', payload, state);
        case PdfActionTypes.SET_SELECTED_SEARCH_RESULT:
            return set('selectedSearchResult', payload, state);
        case PdfActionTypes.CLEAR_PDF_SEARCH_TERM:
            return flow(
                set('pdfSearchTerm', ''),
                set('selectedSearchResult', null),
                set('searchResults', [])
            )(state);
        case PdfActionTypes.CANCEL_SEARCH_SETTINGS:
            return set('searchSettings', state.savedSearchSettings, state);
        case PdfActionTypes.APPLY_SEARCH_SETTINGS:
            return set('savedSearchSettings', state.searchSettings, state);
        case PdfActionTypes.UPDATE_SEARCH_SETTINGS: {
            const { key, value } = payload;
            return set(`searchSettings[${key}]`, value, state);
        }
        case PdfActionTypes.RESET_PDF_SEARCH:
            return flow(
                set('pdfSearchTerm', ''),
                set('selectedSearchResult', null),
                set('searchResults', []),
                set('searchSettings', initialSearchSettings),
                set('savedSearchSettings', initialSearchSettings),
            )(state);
        case PdfActionTypes.RESET_PDF_TEXT:
            return flow(
                set('pdfText', []),
                set('currentPdfPage', 1),
                set('scheduleStartPage', 1),
                set('pageHighlightInfo', undefined)
            )(state);
        case PdfActionTypes.SET_CONTROL_BAR_VIEW:
            return set('controlBarView', payload, state);
        case PdfActionTypes.SET_PDF_NUMBER_OF_PAGES:
            return set('numberOfPages', payload, state);
        case PdfActionTypes.SET_INDEXES_TO_HIGHLIGHT:
            return set('pageHighlightInfo', payload, state);
        case PdfActionTypes.SET_IS_CHECKING_HIGHLIGHT_INDEXES:
            return set('checkingPageHighlights', payload, state);
        case PdfActionTypes.SET_DOCUMENT_VIEWER_TYPE:
            return set('type', payload, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
